export enum AdviserRole {
  User = 'user',
  Assistant = 'assistant',
  Operator = 'operator',
}

export enum AdviserToolName {
  ListTickets = 'list_tickets',
  GetTicketDetails = 'get_ticket_details',
  CreateTicket = 'create_ticket',
  AddTicketComment = 'add_ticket_comment',
  AttachTicketContext = 'attach_ticket_context',
  RequestAttachment = 'request_attachment',
  ShowHighlights = 'show_highlights',
  HandToSupport = 'hand_to_support',
  OfferYesNo = 'offer_yes_no',
}

export const HIGHLIGHTS_CARD_NAME = {
  cubkComplete: 'cubk_complete',
  sortedAutomatically: 'sorted_automatically',
} as const

export enum ToolInvocationState {
  InputAvailable = 'input-available',
  OutputAvailable = 'output-available',
  OutputError = 'output-error',
}

export interface ToolStatusCopy {
  pending?: string
  success: string
  /** Shown when the tool succeeds but had no meaningful payload to act on. */
  emptySuccess?: string
  error: string
}

/** Shared in-progress label for any tool that doesn't override `pending`. */
export const DEFAULT_PENDING = 'Working…'

export const TOOL_STATUS_CONFIG: Record<string, ToolStatusCopy> = {
  [AdviserToolName.AttachTicketContext]: {
    success: 'Shared the full conversation with our support team.',
    emptySuccess: 'Shared your request with our support team.',
    error:
      "Couldn't attach the conversation — our team can still see your request.",
  },
  [AdviserToolName.HandToSupport]: {
    success: 'Connected you with the Heard support team.',
    error: "Couldn't reach the Heard support team.",
  },
}

/** Relative path used by apiAxios (which already has baseURL set) */
export const ADVISER_API_PATH = '/finances/api/v2/adviser'
export const CATCH_UP_MONTHS_PATH =
  '/finances/api/v1/user_documents/statement/catch_up_months'
export const MARK_ACTIVITY_REPORTED_PATH =
  '/finances/api/v1/user_documents/statement/mark_activity_reported'
export const LIMITED_HISTORY_ANSWER_PATH =
  '/finances/api/v1/user_documents/statement/limited_history_answer'

/** Absolute API origin, for callers that bypass apiAxios: SSE fetch and anchor hrefs. */
export const API_BASE =
  process.env.VITE_LOCAL_API_ENDPOINT_V2?.replace(/\/$/, '') ?? ''
export const ADVISER_CHAT_URL = `${API_BASE}${ADVISER_API_PATH}`

export const ADVISER_ASSISTANT_NAME =
  process.env.VITE_ADVISER_ASSISTANT_NAME || 'Haven'

export const HAVEN_TITLE = `Ask ${ADVISER_ASSISTANT_NAME}`

export const ADVISER_COMPLIANCE_DISCLAIMER = `${ADVISER_ASSISTANT_NAME} is AI and may make mistakes. Make sure to check important information.`

export const VIEW_MESSAGES_CHIP = 'View my messages with the Heard team'
export const MESSAGE_TEAM_CHIP = 'Message the Heard team'

export const SUGGESTION_CHIPS = [
  VIEW_MESSAGES_CHIP,
  MESSAGE_TEAM_CHIP,
  'When is my next tax deadline?',
  'Should I become an S corporation?',
] as const

export const MESSAGES_LAUNCH_CARD_TITLE = 'Messages has a new home'
export const MESSAGES_LAUNCH_CARD_TEXT = `Your conversations with the Heard team now live in ${ADVISER_ASSISTANT_NAME}. ${ADVISER_ASSISTANT_NAME} can help right away, but a person is always a message away.`

export const INITIAL_SOURCE_FETCH_DELAY_MS = 1500
export const SOURCE_FETCH_RETRY_DELAY_MS = 2000
export const MAX_SOURCE_FETCH_RETRIES = 3

/** The API's cap on a bookkeeper note: it refuses a longer one with a 400. */
export const BOOKKEEPER_NOTE_MAX_LENGTH = 2000

export const PAGE_PATHS = {
  dashboard: '/dashboard',
  profitLoss: '/reports/profit-loss',
  quarterlyTaxes: '/taxes/quarterly',
} as const

/**
 * Static path-prefix → human label map for Haven page awareness.
 * Known trade-off: duplicates labels from the sidebar nav config and can
 * drift. The ideal end state is a pure route→label resolver shared with
 * useSidebarConfig — deferred follow-up. Longest prefix wins;
 * segment-boundary matching only.
 */
export const PAGE_SECTIONS: Record<string, string> = {
  [PAGE_PATHS.dashboard]: 'Home',
  '/transactions': 'Business transactions',
  [PAGE_PATHS.profitLoss]: 'Profit & Loss',
  [PAGE_PATHS.quarterlyTaxes]: 'Quarterly taxes',
  '/taxes/annual': 'Annual taxes',
  '/taxes/archive': 'Tax Return Archive',
  '/practice/profile': 'Practice profile',
  '/practice/documents': 'Practice documents',
  '/advisory': 'Advisory',
  '/accounts': 'Settings',
}

export const TICKET_CREATED_EXPECTATION_TEXT =
  "I will be escalating your question to our Support Team. They'll follow up once they've had a chance to review. You'll get an email when they reply, and you can read it anytime in Messages."

export const RESOLVED_CASE_STATUS = 'done'

export const CONVERSATION_VIEWS = {
  open: 'open',
  history: 'history',
  needsAttention: 'needs_attention',
} as const

export type ConversationView =
  (typeof CONVERSATION_VIEWS)[keyof typeof CONVERSATION_VIEWS]

export const RAIL_FILTERS = {
  all: 'all',
  needsAttention: 'needs_attention',
} as const

export type RailFilter = (typeof RAIL_FILTERS)[keyof typeof RAIL_FILTERS]
